import { Box, Popper } from '@mui/material';
import moment from 'moment-timezone';
import React, { useEffect, useRef, useState } from 'react';
import {
    Calendar,
    CalendarProps,
    Components,
    EventProps,
    EventWrapperProps,
    Views,
    momentLocalizer,
} from 'react-big-calendar';

import { useDashboardPageContext } from '../../pages/DashboardPage/DashboardPageContext';
import { centerChildren } from '../utils/centering';
import { EVENT_STATE_BG_COLORS, EVENT_STATE_MAIN_COLORS, IEvent } from './LiveView';
import './live-calendar.css';

const getNow = () => new Date();

// Resources must have a timezone property. This is used to localize events in the calendar correctly.
export type AxleResource = {
    uuid: string;
    timezone: string;
};

// constants
const MAX_VISIBLE_EVENTS = 5;
const TIME_DIFF_THRESHOLD = 14; // minutes
const CIRCLE_SIZE = 26;
const CIRCLE_SPACING = 5;

const BASE_EVENT_STYLES = {
    borderRadius: '4px',
    color: 'black',
    border: '2px solid #29CC39',
    fontSize: '12px',
    lineHeight: '16px',
    backgroundColor: '#F4FCF5',
    opacity: 1,
};

// These values are tightly coupled to the timeslot threshold values above.
enum EventDurationThresholds {
    SMALL = 15,
    MEDIUM = 29,
    LARGE = 60,
}

export const LiveCalendar = <T extends IEvent, R extends AxleResource>({
    events,
    backgroundEvents,
    resources,
    noEventsText = undefined,
    isClinicianView,
    ...props
}: {
    events: T[];
    backgroundEvents: T[];
    resources?: R[];
    noEventsText?: string;
    isClinicianView?: boolean;
} & Omit<
    CalendarProps<T, R>,
    'events' | 'backgroundEvents' | 'resources' | 'date' | 'localizer'
>) => {
    const { date } = useDashboardPageContext();
    const dateLocalizer = momentLocalizer(moment);
    const [activeOverflowGroupId, setActiveOverflowGroupId] = useState<string | null>(null);
    const [hoveredEvent, setHoveredEvent] = useState<IEvent | null>(null);
    const [virtualElement, setVirtualElement] = useState<{
        getBoundingClientRect: () => DOMRect;
    } | null>(null);
    const calendarWrapperRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!calendarWrapperRef.current) return;

        // Wait for next browser paint to ensure react-big-calendar has finished its internal
        // rendering and created the .rbc-current-time-indicator element in the DOM
        requestAnimationFrame(() => {
            const currentTimeIndicator = calendarWrapperRef.current?.querySelector(
                '.rbc-current-time-indicator',
            );

            if (!(currentTimeIndicator instanceof HTMLElement)) return;

            // Function to get current time formatted
            const getCurrentTimeTitle = () => {
                const now = moment();
                return `Current time: ${now.format('h:mm A')}`;
            };

            // Set initial title
            currentTimeIndicator.title = getCurrentTimeTitle();

            // Update title on every mouse enter
            const handleMouseEnter = () => {
                currentTimeIndicator.title = getCurrentTimeTitle();
            };

            currentTimeIndicator.addEventListener('mouseenter', handleMouseEnter);

            // Scroll to current time indicator on mount
            currentTimeIndicator.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        });
    }, []); // Empty dependency array - only runs once on mount
    const handleEventMouseEnter = (event: React.MouseEvent, eventData: IEvent) => {
        setHoveredEvent(eventData);
        const rect = event.currentTarget.getBoundingClientRect();
        setVirtualElement({
            getBoundingClientRect: () => rect,
        });
    };

    const handleEventMouseLeave = () => {
        setHoveredEvent(null);
        setVirtualElement(null);
    };

    const getStyleForEvent = (event: IEvent) => ({
        style: {
            ...BASE_EVENT_STYLES,
            border: `2px solid ${EVENT_STATE_MAIN_COLORS[event.needsAttentionState]}`,
            backgroundColor: EVENT_STATE_BG_COLORS[event.needsAttentionState],
        },
    });

    const hasEventPassed = (event: IEvent) => {
        return moment(event.start).isBefore(moment());
    };

    // Process events to handle overflow
    const processedEvents = React.useMemo(() => {
        const eventGroups: T[][] = [];
        const processedEvents = new Set<T>();

        events.forEach((event) => {
            if (processedEvents.has(event)) return;

            const eventStart = new Date(event.start);
            const group = [event];
            processedEvents.add(event);

            events.forEach((otherEvent) => {
                if (processedEvents.has(otherEvent)) return;

                const otherStart = new Date(otherEvent.start);
                const timeDiff =
                    Math.abs(eventStart.getTime() - otherStart.getTime()) / (1000 * 60);

                if (timeDiff <= TIME_DIFF_THRESHOLD) {
                    group.push(otherEvent);
                    processedEvents.add(otherEvent);
                }
            });

            eventGroups.push(group);
        });

        const result: T[] = [];

        // Sort group NEEDS_ATTENTION > MAY_NEED_ATTENTION > NO_ATTENTION_NEEDED
        eventGroups.forEach((group) => {
            const sortedGroup = group.sort((a, b) => {
                return b.needsAttentionState - a.needsAttentionState;
            });

            const visibleEvents = sortedGroup.slice(0, MAX_VISIBLE_EVENTS);
            const hiddenCount = sortedGroup.length - MAX_VISIBLE_EVENTS;
            const groupId = sortedGroup[0].uuid;
            const hiddenEvents = sortedGroup.slice(MAX_VISIBLE_EVENTS).map((e) => ({
                ...e,
                groupId,
            }));

            visibleEvents.forEach((event, index) => {
                result.push({
                    ...event,
                    _horizontalIndex: index,
                    groupId: sortedGroup[0].uuid,
                } as T & { _horizontalIndex: number });
            });

            if (hiddenCount > 0) {
                const firstEvent = visibleEvents[0];
                const overflowEvent = {
                    ...firstEvent,
                    title: `+${hiddenCount}`,
                    uuid: `overflow-${firstEvent.uuid}`,
                    isOverflow: true,
                    hiddenEvents,
                    _horizontalIndex: MAX_VISIBLE_EVENTS,
                    groupId: sortedGroup[0].uuid,
                } as T & { isOverflow: boolean; hiddenEvents: T[]; _horizontalIndex: number };

                result.push(overflowEvent);
            }
        });

        return result;
    }, [events]);

    const renderEventContent = (
        event: IEvent,
        additionalStyle?: Record<string, string | number>,
    ) => {
        const eventColor = EVENT_STATE_MAIN_COLORS[event.needsAttentionState];
        const disciplinesText = event.disciplines?.map((d) => d.toUpperCase()).join(' - ');

        const TitleBadge = ({ marginBottom }: { marginBottom?: string | number }) => (
            <Box
                height={'16px'}
                bgcolor={eventColor}
                color={'white'}
                display={'inline-table'}
                padding={'0 7px'}
                borderRadius={'5px'}
                fontSize={'11px'}
                fontWeight={'500'}
                marginBottom={marginBottom}
            >
                {event.title}
            </Box>
        );

        const StatusDisplay = ({ marginBottom }: { marginBottom?: string | number }) => (
            <Box
                flex={1}
                marginBottom={marginBottom}
                fontWeight={'bold'}
                textTransform={'capitalize'}
            >
                {event.status}
            </Box>
        );

        const PatientInfo = ({ showClinician = false }: { showClinician?: boolean }) => (
            <Box display={'flex'} justifyContent={'space-between'} flexWrap={'wrap'}>
                <Box flex={1}>{event.patientName}</Box>
                {showClinician && <Box>{event.clinicianName}</Box>}
            </Box>
        );

        if (isClinicianView) {
            const padding =
                event.durationMinutes <= EventDurationThresholds.SMALL ? '1px 8px' : '6px 8px';
            const showTitleBadge = event.durationMinutes >= EventDurationThresholds.LARGE;
            const showStatusAndDisciplines =
                event.durationMinutes >= EventDurationThresholds.MEDIUM;
            const showClinician = event.durationMinutes >= EventDurationThresholds.LARGE;
            const titleMarginBottom =
                event.durationMinutes >= EventDurationThresholds.LARGE ? '6px' : '1px';
            const statusMarginBottom =
                event.durationMinutes >= EventDurationThresholds.LARGE ? '6px' : 0;

            return (
                <Box
                    padding={padding}
                    fontSize={'12px'}
                    lineHeight={'16px'}
                    style={additionalStyle}
                    sx={
                        event.durationMinutes <= EventDurationThresholds.MEDIUM
                            ? {
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  flexDirection: 'row-reverse',
                              }
                            : undefined
                    }
                >
                    {showTitleBadge && <TitleBadge marginBottom={titleMarginBottom} />}

                    {showStatusAndDisciplines ? (
                        <Box
                            marginBottom={statusMarginBottom}
                            display={'flex'}
                            justifyContent={'space-between'}
                            flexWrap={'wrap'}
                        >
                            <StatusDisplay marginBottom={0} />
                            <Box>{disciplinesText}</Box>
                        </Box>
                    ) : (
                        <Box>{disciplinesText}</Box>
                    )}

                    <PatientInfo showClinician={showClinician} />
                </Box>
            );
        }

        // Default view (all visits)
        return (
            <Box padding={'8px'} fontSize={'12px'} lineHeight={'16px'} style={additionalStyle}>
                <TitleBadge marginBottom={'8px'} />
                <Box display={'flex'} justifyContent={'space-between'}>
                    <StatusDisplay marginBottom={'8px'} />
                    <Box>{disciplinesText}</Box>
                </Box>
                <Box display={'flex'} justifyContent={'space-between'}>
                    <Box>{event.patientName}</Box>
                    <Box>{event.clinicianName}</Box>
                </Box>
            </Box>
        );
    };

    const getCustomEventWrapper = (props: EventWrapperProps<IEvent>) => {
        return props.event.isOverflow ? (
            <Box
                position={'absolute'}
                top={`calc(${props.style?.top}% - 14px)`}
                display={'flex'}
                flexDirection={'column'}
                width={'100%'}
                zIndex={activeOverflowGroupId === props.event.groupId ? 1 : undefined}
            >
                <Box
                    position={'relative'}
                    right={'4px'}
                    top={6}
                    height={'20px'}
                    width={'34px'}
                    fontSize={'11px'}
                    lineHeight={'16px'}
                    borderRadius={'4px'}
                    boxShadow={'0 1px 2px 0 rgba(0, 0, 0, 0.30), 0 1px 3px 1px rgba(0, 0, 0, 0.15)'}
                    bgcolor={activeOverflowGroupId === props.event.groupId ? '#0062FF' : '#FFFFFF'}
                    color={activeOverflowGroupId === props.event.groupId ? '#FFFFFF' : '#0062FF'}
                    zIndex={activeOverflowGroupId === props.event.groupId ? 1 : 0}
                    alignItems={'center'}
                    fontWeight={'500'}
                    alignSelf={'flex-end'}
                    display={'flex'}
                    justifyContent={'center'}
                    role='button'
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                        if (activeOverflowGroupId === props.event.groupId) {
                            setActiveOverflowGroupId(null);
                        } else {
                            props.event.hiddenEvents &&
                                props.event.groupId &&
                                setActiveOverflowGroupId(props.event.groupId);
                        }
                    }}
                >
                    {`+ ${props.event.hiddenEvents?.length}`}
                </Box>
                {activeOverflowGroupId === props.event.groupId && props.event.hiddenEvents && (
                    <Box
                        paddingTop={`calc(${CIRCLE_SIZE}px + ${CIRCLE_SPACING}px)`}
                        position={'relative'}
                        top={'-20px'}
                        bgcolor={'#E6E6E6'}
                        display={'grid'}
                        borderRadius={'4px'}
                        gridTemplateColumns={`repeat(${MAX_VISIBLE_EVENTS + 1}, ${CIRCLE_SIZE}px)`}
                        gap={'5px'}
                    >
                        {props.event.hiddenEvents.map((overflowEvent: IEvent) => (
                            <Box
                                key={overflowEvent.uuid}
                                onMouseEnter={(event) =>
                                    handleEventMouseEnter(event, overflowEvent)
                                }
                                onMouseLeave={handleEventMouseLeave}
                                sx={{
                                    backgroundColor:
                                        EVENT_STATE_MAIN_COLORS[overflowEvent.needsAttentionState],
                                    width: `${CIRCLE_SIZE}px`,
                                    height: `${CIRCLE_SIZE}px`,
                                    borderRadius: '50%',
                                    opacity: hasEventPassed(overflowEvent) ? 0.3 : 1,
                                    '&:hover': {
                                        border: '2px solid black',
                                    },
                                }}
                            />
                        ))}
                    </Box>
                )}
            </Box>
        ) : (
            <Box
                onMouseEnter={(event) => handleEventMouseEnter(event, props.event)}
                onMouseLeave={handleEventMouseLeave}
                zIndex={activeOverflowGroupId === props.event.groupId ? 1 : undefined}
                sx={{
                    position: 'absolute',
                    top: `calc(${props.style?.top}% - 14px)`,
                    left: `calc(${props.event._horizontalIndex} * (${CIRCLE_SIZE}px + ${CIRCLE_SPACING}px))`,
                    backgroundColor: EVENT_STATE_MAIN_COLORS[props.event.needsAttentionState],
                    width: `${CIRCLE_SIZE}px`,
                    height: `${CIRCLE_SIZE}px`,
                    borderRadius: '50%',
                    opacity: hasEventPassed(props.event) ? 0.3 : 1,
                    '&:hover': {
                        border: '2px solid black',
                    },
                }}
            />
        );
    };

    const calendarComponents = {
        event: (props: EventProps<IEvent>) => renderEventContent(props.event),
        eventWrapper: isClinicianView ? undefined : getCustomEventWrapper,
    } as Components<T, R>;

    return events.length === 0 && backgroundEvents.length === 0 && noEventsText ? (
        <Box height={'100%'} {...centerChildren}>
            {noEventsText}
        </Box>
    ) : (
        <>
            <Box className='live-calendar-wrapper' ref={calendarWrapperRef}>
                <Calendar
                    backgroundEvents={[]}
                    localizer={dateLocalizer}
                    events={processedEvents}
                    resources={resources}
                    defaultDate={date}
                    showMultiDayTimes={true}
                    getNow={getNow}
                    popup={true}
                    dayLayoutAlgorithm={'no-overlap'}
                    eventPropGetter={getStyleForEvent}
                    style={{ zIndex: 1, position: 'relative' }}
                    components={calendarComponents}
                    timeslots={4}
                    step={15}
                    defaultView={Views.DAY}
                    views={[Views.DAY]}
                    toolbar={false}
                    {...props}
                />
            </Box>
            {hoveredEvent && virtualElement && (
                <Popper
                    open={!!hoveredEvent}
                    anchorEl={virtualElement}
                    placement='right-end'
                    sx={{ zIndex: 2, width: '215px' }}
                >
                    {renderEventContent(hoveredEvent, getStyleForEvent(hoveredEvent).style)}
                </Popper>
            )}
        </>
    );
};
